import React, { useEffect, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";
import { resolveMediaUrl } from "@/components/FileUpload";
import { fetchAudioPeaks, isLocalMediaUrl } from "@/lib/audioPeaks";

const cache = new Map(); // url -> normalized peaks[]
const N = 56;

// Décodage sur l'appareil réservé aux fichiers LOCAUX (blob:/data:, quelques secondes) — lot 684.
const decodeLocalPeaks = async (src) => {
  const res = await fetch(src);
  const buf = await res.arrayBuffer();
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) throw new Error("no-audiocontext");
  const ac = new Ctx();
  try {
    const audio = await ac.decodeAudioData(buf);
    const ch = audio.getChannelData(0);
    const block = Math.max(1, Math.floor(ch.length / N));
    const out = [];
    for (let i = 0; i < N; i++) {
      let sum = 0;
      for (let j = 0; j < block; j++) { const v = ch[i * block + j] || 0; sum += v * v; }
      out.push(Math.sqrt(sum / block));
    }
    const max = Math.max(...out, 0.0001);
    return out.map((v) => Math.max(0.08, v / max));
  } finally {
    try { ac.close(); } catch { /* noop */ }
  }
};

// Mini waveform for the music extract: ~56 amplitude bars, dims the part before the chosen start, and lets the
// author click anywhere to set the start point (helps spot the chorus). Lot 684 : pour un morceau du serveur, la forme
// d'onde est calculée côté serveur (plus de chanson de 4 min décodée en mémoire sur le téléphone).
export const Waveform = ({ url, start = 0, duration = 0, onSeek, className = "", testId = "waveform" }) => {
  const { t } = useApp();
  const [peaks, setPeaks] = useState(url && cache.has(url) ? cache.get(url) : null);
  const barRef = useRef(null);

  useEffect(() => {
    let alive = true;
    if (!url) { setPeaks(null); return undefined; }
    if (cache.has(url)) { setPeaks(cache.get(url)); return undefined; }
    (isLocalMediaUrl(url) ? decodeLocalPeaks(resolveMediaUrl(url)) : fetchAudioPeaks(url, N).then((r) => r.peaks))
      .then((norm) => { cache.set(url, norm); if (alive) setPeaks(norm); })
      .catch(() => { if (alive) setPeaks([]); });
    return () => { alive = false; };
  }, [url]);

  if (!peaks || peaks.length === 0) return null;
  const startFrac = duration > 0 ? Math.min(1, (start || 0) / duration) : 0;

  const handleClick = (e) => {
    if (!onSeek || !duration || !barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onSeek(Math.round(frac * duration * 2) / 2);
  };

  return (
    <div ref={barRef} onClick={handleClick} className={`flex items-end gap-[2px] h-8 cursor-pointer select-none ${className}`} data-testid={testId} style={{ minHeight: 32 }} title={t("audio.pick_start", "Cliquez pour choisir le départ")}>
      {peaks.map((p, i) => {
        const active = (i / peaks.length) >= startFrac;
        return (
          <span
            key={i}
            className="flex-1 rounded-sm"
            style={{ height: `${Math.round(p * 100)}%`, background: active ? "var(--yalola-primary)" : "var(--yalola-border)", opacity: active ? 1 : 0.5 }}
          />
        );
      })}
    </div>
  );
};

export default Waveform;
